/**
 * ContactsManager — Admin panel for inviting/managing customer contacts
 *
 * Rendered as a tab inside PlanDetailPage. Lets CSMs invite external
 * contacts to a limited portal view of the onboarding plan.
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Users, Plus, Trash2, Mail, Copy, CheckCircle, Loader2, UserPlus,
  Shield, ExternalLink,
} from 'lucide-react';
import {
  inviteContact, listPlanContacts, revokeContactAccess,
  type PlanContact,
} from '@/services/api/onboardingPortal';

interface Props {
  planId: number;
}

export default function ContactsManager({ planId }: Props) {
  const [contacts, setContacts] = useState<PlanContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  // Invite form
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [accessLevel, setAccessLevel] = useState('view');
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ invitation_url: string; email: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPlanContacts(planId);
      setContacts(data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [planId]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async () => {
    setInviting(true);
    try {
      const result = await inviteContact(planId, {
        email,
        name,
        access_level: accessLevel,
      });
      setInviteResult({ invitation_url: result.invitation_url, email: result.email });
      load();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to invite contact');
    }
    setInviting(false);
  };

  const handleRevoke = async (userId: number, contactName: string) => {
    if (!confirm(`Remove ${contactName}'s access to this plan?`)) return;
    try {
      await revokeContactAccess(planId, userId);
      load();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to revoke access');
    }
  };

  const copyInviteUrl = (url: string) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(url);
    setTimeout(() => setCopied(null), 2000);
  };

  const accessBadgeColor: Record<string, string> = {
    view: 'bg-gray-100 text-gray-700',
    comment: 'bg-blue-100 text-blue-700',
    update_status: 'bg-green-100 text-green-700',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-sm text-gray-500 flex items-center gap-1.5">
          <Users className="h-4 w-4" />
          Customer Contacts ({contacts.length})
        </h3>
        <Button size="sm" onClick={() => { setShowInvite(true); setInviteResult(null); }}>
          <UserPlus className="h-4 w-4 mr-1" />
          Invite Contact
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : contacts.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Users className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No contacts invited yet</p>
            <p className="text-xs text-gray-400 mt-1">Invite customer contacts so they can track onboarding progress</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {contacts.map(contact => (
            <Card key={contact.user_id} className="overflow-hidden">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-medium">{contact.name}</span>
                      <Badge className={accessBadgeColor[contact.access_level] || 'bg-gray-100'} >
                        <Shield className="h-3 w-3 mr-0.5" />
                        {contact.access_level}
                      </Badge>
                      {contact.email_verified ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          <CheckCircle className="h-3 w-3 mr-0.5" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Pending</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {contact.email}
                      </span>
                      <span>Invited {new Date(contact.granted_at).toLocaleDateString()}</span>
                      {contact.granted_by_name && <span>by {contact.granted_by_name}</span>}
                    </div>
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => handleRevoke(contact.user_id, contact.name)}
                    className="text-red-500 hover:text-red-700 flex-shrink-0"
                    title="Remove access"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Invite dialog */}
      {showInvite && (
        <Dialog open onOpenChange={() => setShowInvite(false)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Invite Customer Contact</DialogTitle>
              <DialogDescription>
                They will receive an invitation link to set up their account and view this plan.
              </DialogDescription>
            </DialogHeader>

            {inviteResult ? (
              <div className="space-y-4">
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Invitation created for <strong>{inviteResult.email}</strong>
                  </AlertDescription>
                </Alert>
                <div>
                  <Label>Invitation Link</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      readOnly
                      value={`${window.location.origin}${inviteResult.invitation_url}`}
                      className="text-xs"
                    />
                    <Button
                      variant="outline" size="sm"
                      onClick={() => copyInviteUrl(inviteResult.invitation_url)}
                    >
                      {copied === inviteResult.invitation_url ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Share this link with the contact. It expires in 7 days.</p>
                </div>
                <DialogFooter>
                  <Button onClick={() => { setShowInvite(false); setInviteResult(null); setEmail(''); setName(''); }}>
                    Done
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input value={name} onChange={e => setName(e.target.value)} placeholder="Contact's full name" autoFocus />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="contact@customer.com" />
                  </div>
                  <div>
                    <Label>Access Level</Label>
                    <Select value={accessLevel} onValueChange={setAccessLevel}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="view">View Only</SelectItem>
                        <SelectItem value="comment">Can Comment</SelectItem>
                        <SelectItem value="update_status">Can Update Status</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-400 mt-1">
                      {accessLevel === 'view' && 'Contact can see plan progress but cannot make changes'}
                      {accessLevel === 'comment' && 'Contact can add notes to tasks'}
                      {accessLevel === 'update_status' && 'Contact can update task statuses and add notes'}
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
                  <Button onClick={handleInvite} disabled={inviting || !email || !name}>
                    {inviting ? 'Sending...' : 'Send Invitation'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
